<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { useRoute } from 'vue-router'
import { ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'

interface NavItem {
  name: string
  to?: string
  icon: any
  exact?: boolean
  children?: NavItem[]
  beta?: boolean
  oldBackofficeLink?: string
  badge?: number
}

interface MenuPosition {
  top: string
  bottom: string
  left: string
  maxHeight: string
  overflowY: 'auto' | 'visible' | 'hidden' | 'scroll'
}

defineProps<{
  items: NavItem[]
  isCollapsed?: boolean
}>()
const emit = defineEmits<(e: 'itemClicked') => void>()
const route = useRoute()

const hoveredItem = ref<string | null>(null)
const menuPosition = ref<MenuPosition>({
  top: '0px',
  bottom: 'auto',
  left: '0px',
  maxHeight: '90vh',
  overflowY: 'auto',
})

const onItemEnter = (event: MouseEvent, item: NavItem) => {
  const target = event.currentTarget as HTMLElement
  if (target) {
    const rect = target.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const menuHeight = item.children ? item.children.length * 40 + 100 : 400 // Calculate based on number of items

    // If menu would go below viewport, adjust the position
    const wouldOverflow = rect.top + menuHeight > viewportHeight
    const topOffset = wouldOverflow
      ? Math.min(rect.top, viewportHeight - menuHeight)
      : rect.top

    menuPosition.value = {
      top: topOffset + 'px',
      bottom: 'auto',
      left: rect.right - 4 + 'px',
      maxHeight: '90vh',
      overflowY: 'auto',
    }
  }
  hoveredItem.value = item.name
}

// Add a method to get menu height for an item
const getMenuHeight = (item: NavItem) => {
  if (!item.children) return 0
  return item.children.length * 20 + 200 // 40px per item + 100px for padding and header
}
</script>

<template>
  <nav
    class="flex-1 space-y-1 bg-secondary-900"
    :class="{
      'px-6': !isCollapsed,
      'px-0': isCollapsed,
      'pt-4': isCollapsed,
      'pt-0': !isCollapsed,
    }"
    aria-label="Sidebar"
  >
    <template v-for="item in items" :key="item.name">
      <NuxtLink
        v-if="!item.children"
        :to="{ name: item.to }"
        exact
        :class="[
          'link bg-secondary-900 text-gray-300 hover:bg-secondary-800 hover:text-white group w-full flex items-center py-4 md:py-2 text-sm font-medium rounded-lg cursor-pointer relative',
          { 'justify-center px-6': isCollapsed, 'pl-2': !isCollapsed },
        ]"
        @click="emit('itemClicked')"
      >
        <div
          v-if="isCollapsed"
          v-tooltip.right="item.name"
          :class="[item.icon, 'icon', 'text-gray-300']"
          aria-hidden="true"
        />
        <div
          v-else
          :class="[item.icon, 'icon', 'text-gray-300']"
          aria-hidden="true"
        />
        <span v-if="!isCollapsed" class="flex items-center">
          {{ item.name }}
          <span
            v-if="item.badge"
            class="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white"
            >{{ item.badge > 99 ? '99+' : item.badge }}</span
          >
          <span
            v-if="item.beta"
            class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-primary-500 text-white"
            >Beta</span
          >
          <a
            v-if="item.oldBackofficeLink"
            :href="item.oldBackofficeLink"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-500 text-white hover:bg-gray-600 cursor-pointer"
            @click.stop
          >
            Old BO
          </a>
        </span>
      </NuxtLink>
      <div
        v-else
        class="relative"
        @mouseenter="(e) => onItemEnter(e, item)"
        @mouseleave="hoveredItem = null"
      >
        <div
          v-if="isCollapsed"
          class="link bg-secondary-900 text-gray-300 hover:bg-secondary-800 hover:text-white group w-full flex items-center justify-center px-6 py-4 md:py-3 text-sm font-medium rounded-lg cursor-pointer relative"
        >
          <div
            :class="[item.icon, 'icon', 'text-gray-300']"
            class="flex-shrink-0 h-6 w-6 group-hover:text-white"
            aria-hidden="true"
          />
          <Transition
            enter="transition ease-out duration-200"
            enter-from="opacity-0 translate-x-1"
            enter-to="opacity-100 translate-x-0"
            leave="transition ease-in duration-150"
            leave-from="opacity-100 translate-x-0"
            leave-to="opacity-0 translate-x-1"
          >
            <div
              v-if="hoveredItem === item.name"
              class="fixed -ml-1 bg-secondary-800 rounded-md text-white whitespace-nowrap z-[100] min-w-[200px] shadow-lg overflow-y-auto scrollbar:!w-1.5 scrollbar:!h-1.5 scrollbar-track:!bg-secondary-900 scrollbar-thumb:!rounded-full scrollbar-thumb:!bg-secondary-700"
              :style="{
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                maxHeight: menuPosition.maxHeight,
                overflowY: menuPosition.overflowY,
              }"
            >
              <div
                class="py-2 px-3 text-sm font-medium border-b border-secondary-700"
              >
                <span class="flex items-center">
                  {{ item.name }}
                  <span
                    v-if="item.beta"
                    class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-primary-500 text-white"
                    >Beta</span
                  >
                  <a
                    v-if="item.oldBackofficeLink"
                    :href="item.oldBackofficeLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-500 text-white hover:bg-gray-600 cursor-pointer"
                    @click.stop
                  >
                    Old BO
                  </a>
                </span>
              </div>
              <div class="py-2">
                <NuxtLink
                  v-for="subItem in item.children"
                  :key="subItem.name"
                  class="flex items-center w-full px-3 py-2 text-sm hover:bg-secondary-700"
                  :to="{ name: subItem.to }"
                  :class="[
                    [
                      subItem.to,
                      subItem.to + '-id',
                      subItem.to + '-new',
                    ].includes(String(route.name))
                      ? 'bg-secondary-700'
                      : '',
                    'justify-start',
                  ]"
                  @click="emit('itemClicked')"
                >
                  <div
                    :class="[subItem.icon, 'icon']"
                    aria-hidden="true"
                    class="text-gray-300 mr-2 flex-shrink-0 w-6"
                  />
                  <span
                    class="flex-1 text-left whitespace-nowrap flex items-center"
                  >
                    {{ subItem.name }}
                    <span
                      v-if="subItem.badge"
                      class="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white"
                      >{{ subItem.badge > 99 ? '99+' : subItem.badge }}</span
                    >
                    <span
                      v-if="subItem.beta"
                      class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-primary-500 text-white"
                      >Beta</span
                    >
                    <a
                      v-if="subItem.oldBackofficeLink"
                      :href="subItem.oldBackofficeLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-500 text-white hover:bg-gray-600 cursor-pointer"
                      @click.stop
                    >
                      Old BO
                    </a>
                  </span>
                </NuxtLink>
              </div>
            </div>
          </Transition>
        </div>
        <Disclosure
          v-else
          v-slot="{ open }"
          as="div"
          class="space-y-1"
          :default-open="
            !!item.children.find((subItem) =>
              String(route.name).startsWith(subItem.to),
            )
          "
        >
          <DisclosureButton
            class="link bg-secondary-900 text-gray-300 hover:bg-secondary-800 hover:text-white group w-full flex items-center pl-2 py-4 md:py-2 text-sm font-medium rounded-lg cursor-pointer"
          >
            <div
              :class="item.icon"
              class="icon text-xl mr-3 flex-shrink-0 h-6 w-6 text-gray-300 group-hover:text-white"
              aria-hidden="true"
            />
            <span class="flex-1 text-left flex items-center">
              {{ item.name }}
              <span
                v-if="item.badge"
                class="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white"
                >{{ item.badge > 99 ? '99+' : item.badge }}</span
              >
              <span
                v-if="item.beta"
                class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-primary-500 text-white"
                >Beta</span
              >
              <a
                v-if="item.oldBackofficeLink"
                :href="item.oldBackofficeLink"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-500 text-white hover:bg-gray-600 cursor-pointer"
                @click.stop
              >
                Old BO
              </a>
            </span>
            <div
              :class="[
                open ? 'text-gray-600 -rotate-90' : 'text-gray-500 rotate-90',
                'i-heroicons-solid-chevron-right text-lg ml-3 mr-1 flex-shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150',
              ]"
            />
          </DisclosureButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <DisclosurePanel class="space-y-1">
              <template v-for="subItem in item.children" :key="subItem.name">
                <NuxtLink
                  class="link bg-secondary-900 text-gray-300 hover:bg-secondary-800 hover:text-white group w-full flex items-center pl-11 py-4 md:py-2 text-sm font-medium rounded-lg cursor-pointer"
                  :to="{ name: subItem.to }"
                  :class="[
                    [
                      subItem.to,
                      subItem.to + '-id',
                      subItem.to + '-new',
                    ].includes(String(route.name))
                      ? 'router-link-root-active'
                      : '',
                    'justify-start',
                  ]"
                  @click="emit('itemClicked')"
                >
                  <div
                    :class="[subItem.icon, 'icon']"
                    aria-hidden="true"
                    class="text-gray-300 flex-shrink-0 w-6"
                  />
                  <span
                    class="flex-1 text-left whitespace-nowrap flex items-center"
                  >
                    {{ subItem.name }}
                    <span
                      v-if="subItem.badge"
                      class="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-500 text-white"
                      >{{ subItem.badge > 99 ? '99+' : subItem.badge }}</span
                    >
                    <span
                      v-if="subItem.beta"
                      class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-primary-500 text-white"
                      >Beta</span
                    >
                    <a
                      v-if="subItem.oldBackofficeLink"
                      :href="subItem.oldBackofficeLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-500 text-white hover:bg-gray-600 cursor-pointer"
                      @click.stop
                    >
                      Old BO
                    </a>
                  </span>
                </NuxtLink>
              </template>
            </DisclosurePanel>
          </transition>
        </Disclosure>
      </div>
    </template>
  </nav>
</template>

<style scoped lang="postcss">
.router-link-root-active,
.router-link-exact-active {
  @apply bg-secondary-800 text-white;
}
.link > .icon {
  @apply text-xl mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-white;
}
.router-link-root-active > .icon,
.router-link-exact-active > .icon {
  @apply text-primary-500;
}

/* Show tooltips on hover */
.group:hover .tooltip {
  @apply opacity-100;
}

/* Show submenu on hover */
.group\/nav:hover .submenu {
  @apply opacity-100;
}
</style>
